/**
 * Opportunities Service - Firestore operations for SAM.gov opportunities
 *
 * Data Source: SAM.gov (exclusive source for NEW opportunities)
 * Historical Data: Related awards from USAspending + FPDS
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../../../config/firebase";

// Updated collection names for correct data architecture
const SAM_OPPORTUNITIES_COLLECTION = "sam_opportunities";
const HISTORICAL_AWARDS_COLLECTION = "historical_awards";
const RELATED_AWARDS_COLLECTION = "related_awards_by_opportunity";

/**
 * Fetch opportunities from SAM.gov collection with optional filters
 *
 * Note: Firestore has limitations on compound queries. For complex filtering,
 * we fetch more data and filter client-side.
 */
export async function getOpportunities({
  isNew = null,
  similarityBand = null,
  naicsCode = null,
  department = null,
  setAside = null,
  sortBy = "posted_date",
  sortDirection = "desc",
  pageSize = 50,
  lastDoc = null,
} = {}) {
  const opportunitiesRef = collection(db, SAM_OPPORTUNITIES_COLLECTION);
  const constraints = [];

  // Apply Firestore-compatible filters (limited due to index requirements)
  // We'll do most filtering client-side to avoid complex index requirements

  // Order by the selected sort field
  const validSortFields = ["posted_date", "response_deadline", "title"];
  const sortField = validSortFields.includes(sortBy) ? sortBy : "posted_date";
  constraints.push(orderBy(sortField, sortDirection));
  constraints.push(limit(pageSize * 2)); // Fetch extra to account for client-side filtering

  // Pagination
  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const q = query(opportunitiesRef, ...constraints);
  const snapshot = await getDocs(q);

  let opportunities = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    // Map SAM.gov fields to display fields
    summary_title: doc.data().title,
    agency_name: doc.data().department_name || doc.data().department,
    office_name: doc.data().office_name || doc.data().sub_tier_name,
    effective_date: doc.data().posted_date,
    estimated_value: doc.data().award?.amount || null,
  }));

  // Client-side filtering for complex queries
  if (isNew !== null) {
    opportunities = opportunities.filter(opp => opp.is_new === isNew);
  }
  if (similarityBand) {
    opportunities = opportunities.filter(opp => opp.similarity_band === similarityBand);
  }
  if (naicsCode) {
    opportunities = opportunities.filter(opp =>
      opp.naics_code === naicsCode ||
      (opp.naics_codes && opp.naics_codes.includes(naicsCode))
    );
  }
  if (department) {
    opportunities = opportunities.filter(opp =>
      opp.department_name === department || opp.department === department
    );
  }
  if (setAside) {
    opportunities = opportunities.filter(opp =>
      opp.set_aside === setAside ||
      opp.set_aside_description?.toLowerCase().includes(setAside.toLowerCase())
    );
  }

  // Trim to requested page size
  const trimmedOpportunities = opportunities.slice(0, pageSize);

  return {
    opportunities: trimmedOpportunities,
    lastDoc: snapshot.docs[snapshot.docs.length - 1] || null,
    hasMore: opportunities.length > pageSize || snapshot.docs.length === pageSize * 2,
  };
}

/**
 * Get all distinct set-aside types for filter dropdown
 */
export async function getDistinctSetAsides() {
  const opportunitiesRef = collection(db, SAM_OPPORTUNITIES_COLLECTION);
  const snapshot = await getDocs(opportunitiesRef);

  const setAsides = new Map(); // code -> description
  snapshot.docs.forEach((doc) => {
    const data = doc.data();
    if (data.set_aside) {
      setAsides.set(data.set_aside, data.set_aside_description || data.set_aside);
    }
  });

  return Array.from(setAsides.entries())
    .map(([code, description]) => ({ code, description }))
    .sort((a, b) => a.description.localeCompare(b.description));
}

/**
 * Fetch a single opportunity by ID
 */
export async function getOpportunityById(id) {
  const docRef = doc(db, SAM_OPPORTUNITIES_COLLECTION, id);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();

  return {
    id: snapshot.id,
    ...data,
    // Map SAM.gov fields
    summary_title: data.title,
    summary_description: data.description,
    agency_name: data.department_name || data.department,
    office_name: data.office_name || data.sub_tier_name,
    effective_date: data.posted_date,
    response_deadline: data.response_deadline,
  };
}

/**
 * Fetch related historical awards for an opportunity
 */
export async function getRelatedAwards(opportunityId) {
  const docRef = doc(db, RELATED_AWARDS_COLLECTION, opportunityId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return [];
  }

  return snapshot.data().related_awards || [];
}

/**
 * Fetch a historical award by PIID
 */
export async function getHistoricalAward(awardId) {
  const docRef = doc(db, HISTORICAL_AWARDS_COLLECTION, awardId);
  const snapshot = await getDoc(docRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  };
}

/**
 * Get distinct NAICS codes from opportunities for filter dropdown
 */
export async function getDistinctNaicsCodes() {
  const opportunitiesRef = collection(db, SAM_OPPORTUNITIES_COLLECTION);
  const snapshot = await getDocs(opportunitiesRef);

  const naicsCodes = new Set();
  snapshot.docs.forEach((doc) => {
    const data = doc.data();
    if (data.naics_code) {
      naicsCodes.add(data.naics_code);
    }
    // Also check naics_codes array (SAM.gov can have multiple)
    if (data.naics_codes) {
      data.naics_codes.forEach((code) => naicsCodes.add(code));
    }
  });

  return Array.from(naicsCodes).sort();
}

/**
 * Get distinct departments from opportunities for filter dropdown
 */
export async function getDistinctDepartments() {
  const opportunitiesRef = collection(db, SAM_OPPORTUNITIES_COLLECTION);
  const snapshot = await getDocs(opportunitiesRef);

  const departments = new Set();
  snapshot.docs.forEach((doc) => {
    const data = doc.data();
    const dept = data.department_name || data.department;
    if (dept) {
      departments.add(dept);
    }
  });

  return Array.from(departments).sort();
}

/**
 * Get opportunity counts by similarity band
 */
export async function getOpportunityStats() {
  const opportunitiesRef = collection(db, SAM_OPPORTUNITIES_COLLECTION);
  const snapshot = await getDocs(opportunitiesRef);

  const stats = {
    total: snapshot.size,
    new: 0,
    active: 0,
    byBand: {
      high: 0,
      medium: 0,
      low: 0,
      none: 0,
    },
  };

  const now = new Date();

  snapshot.docs.forEach((doc) => {
    const data = doc.data();

    if (data.is_new) stats.new++;

    // Check if response deadline hasn't passed
    if (data.response_deadline) {
      const deadline = data.response_deadline.toDate
        ? data.response_deadline.toDate()
        : new Date(data.response_deadline);
      if (deadline > now) {
        stats.active++;
      }
    } else {
      stats.active++; // No deadline = assume active
    }

    const band = data.similarity_band || "none";
    if (stats.byBand[band] !== undefined) {
      stats.byBand[band]++;
    }
  });

  return stats;
}

/**
 * Format currency for display
 */
export function formatCurrency(value) {
  if (!value && value !== 0) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format date for display
 */
export function formatDate(dateValue) {
  if (!dateValue) return "N/A";

  // Handle Firestore Timestamp
  const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

  // Check for invalid date
  if (isNaN(date.getTime())) return "N/A";

  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

/**
 * Format deadline with urgency indicator
 */
export function formatDeadline(dateValue) {
  if (!dateValue) return { text: "No deadline", urgent: false };

  const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

  if (isNaN(date.getTime())) return { text: "N/A", urgent: false };

  const now = new Date();
  const daysUntil = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

  const text = formatDate(dateValue);

  if (daysUntil < 0) {
    return { text: `Closed ${text}`, urgent: false, closed: true };
  } else if (daysUntil <= 7) {
    return { text: `${text} (${daysUntil} days)`, urgent: true };
  } else if (daysUntil <= 14) {
    return { text: `${text} (${daysUntil} days)`, urgent: false };
  }

  return { text, urgent: false };
}
